
import React from 'react';
import { useRoute } from '@react-navigation/native';
import { NC_FILE_POPUP_CONFIG, OPERATOR_PANEL_INFORMATION_SCREENS, scheduledProductionOperationIdParam } from '@components/operatorPanel/constants/operatorPanel.constants';
import { getLinkingModalConfig, isWeb, navigateTo, Popup } from '@helpers';
import { useSelector } from 'react-redux';
import { selectIsWithERP } from '@reducers/membership';
import { buildOPTabRequirementsContext } from '@components/operatorPanel/helpers/buildOPTabRequirementsContext';
import { getInitialOPInformationScreen } from '@components/operatorPanel/helpers/getInitialOPInformationScreen';
import { HomePageBottomPartProps } from '@components/operatorPanel/components/HomePage/HomePageBottomPart';
import OperatorPanelNCFileType from '@components/operatorPanel/components/elements/OperatorPanelNCFileType';
import { fullWidth, scaleOP, width } from '@components/frame';
import { fontOP } from '@styles/fonts';
import { BOTTOM_SHEET_TOP_OFFSET } from '@v2/pages/common/constants/base.constants';
import ProductionInformation from '@components/productionInformation';
import { selectSelectedItem } from '@reducers/machines';

interface UseManageHomePageButtonsPressParams
  extends Pick<HomePageBottomPartProps, | 'ncFileId' | 'ncFileName' | 'productionBatchId' | 'CC' | 'scale' | 'font'> {
		isNcFileContentAvailable?: boolean;
		onActionDone?: () => void;
	}

export const useManageHomePageButtonsPress = ({
  ncFileId,
  ncFileName,
  productionBatchId,
  CC,
	isNcFileContentAvailable,
	onActionDone,
  scale = scaleOP,
  font = fontOP,
}: UseManageHomePageButtonsPressParams) => {
  const route = useRoute();
  const isWithERP = useSelector(selectIsWithERP);
  const selectedMachine = useSelector(selectSelectedItem);

  const informationSubScreen = getInitialOPInformationScreen(
    buildOPTabRequirementsContext({
      isWithERP,
      isMachinePerspective: route?.params?.[scheduledProductionOperationIdParam] == null,
      ncFileStatus: selectedMachine?.machineStatus?.ncFileStatus,
    })
  );

  const onInformationPress = () => {
    navigateTo(OPERATOR_PANEL_INFORMATION_SCREENS.Information, { screen: informationSubScreen, params: { id: route?.params?.id } });
  };

  const onScheduledPress = () => {
    navigateTo('OperatorPanelScheduledScreen', { id: route?.params?.id });
  };

  const onNCFilesPress = () => {
    navigateTo(OPERATOR_PANEL_INFORMATION_SCREENS.Information, {
      screen: OPERATOR_PANEL_INFORMATION_SCREENS.SearchNCFiles,
      params: { id: route?.params?.id },
    });
  };

  const onModifyNCFileTypePress = (onActionStart?: () => void) => {
    const cachedValues = {
      // to use the same values while linking process
      selectedItem: { ...selectedMachine },
      ncFileId,
      ncFileName,
      ...(productionBatchId ? { productionBatchId } : {}),
    };

    Popup({
      Component: (p: any) => (
        <OperatorPanelNCFileType
          {...{
            ...p,
            ...cachedValues,
            CC,
            onActionStart,
            scale,
            font,
						isNcFileContentAvailable,
						onActionDone,
          }}
        />
      ),
      ...getLinkingModalConfig(scale),
      offsetTop: isWeb ? scale(NC_FILE_POPUP_CONFIG.verticalOffset) : scale(BOTTOM_SHEET_TOP_OFFSET / 2),
      offsetBottom: scale(NC_FILE_POPUP_CONFIG.verticalOffset),
      width:
        (isWeb ? fullWidth : width) -
        scale((isWeb ? fullWidth : width) > NC_FILE_POPUP_CONFIG.maxWidthBreakPoint ? NC_FILE_POPUP_CONFIG.widthOffsetInCaseBreakpoint : 0),
    });
  };

  const onViewNCFilePress = () => {
    navigateTo('OperatorPanelViewNCFile', { ncFileId, ncFileName, id: selectedMachine.machineId });
  };

  const onLinkToJobPress = (onActionStart?: () => void) => {
    const cachedValues = {
      // to use the same values while linking process
      selectedItem: { ...selectedMachine },
      productionBatchId: productionBatchId,
      selectedNCFileId: ncFileId,
    };

    Popup({
      Component: (p: any) => (
        <ProductionInformation
          {...{
            ...p,
            ...cachedValues,
            scale,
            font,
            isOperatorPanel: true,
            title: 'Link Batch to Job',
            onActionStart,
          }}
        />
      ),
      ...getLinkingModalConfig(scale),
      offsetTop: isWeb ? scale(NC_FILE_POPUP_CONFIG.verticalOffset) : scale(BOTTOM_SHEET_TOP_OFFSET / 2),
      offsetBottom: scale(NC_FILE_POPUP_CONFIG.verticalOffset),
      width:
        (isWeb ? fullWidth : width) -
        scale((isWeb ? fullWidth : width) > NC_FILE_POPUP_CONFIG.maxWidthBreakPoint ? NC_FILE_POPUP_CONFIG.widthOffsetInCaseBreakpoint : 0),
    });
  };

  return {
    onInformationPress,
    onScheduledPress,
    onNCFilesPress,
    onModifyNCFileTypePress,
    onViewNCFilePress,
    onLinkToJobPress,
  };
};
